/**
 * Level progression for the endless run. Pure data + functions.
 *
 * The world is one endless strip divided into levels of LEVEL_LENGTH_PX. Each level takes a
 * template (theme + feature set + difficulty band); after the last template the cycle repeats
 * with a harder band, so the run never ends but keeps escalating.
 */

export interface LevelTheme {
  id: string;
  /** Sky gradient top/bottom. */
  sky: [number, number];
  grass: number;
  soil: number;
  soilDark: number;
  stone: number;
  stoneDark: number;
  plank: number;
  plankLight: number;
  /** HUD/banner accent. */
  accent: number;
}

export type Feature =
  | 'pit'
  | 'ledge'
  | 'terrace'
  | 'spikes'
  | 'moving'
  | 'crumble'
  | 'spring'
  | 'golden'
  | 'roller'
  | 'beetle'
  | 'bat'
  | 'rock'
  | 'ice'
  | 'wind';

export interface LevelTemplate {
  name: string;
  subtitle: string;
  theme: LevelTheme;
  features: readonly Feature[];
  /** Difficulty at the level start and end (0..1) for the first cycle. */
  difficulty: readonly [number, number];
}

export interface LevelDef extends LevelTemplate {
  /** 0-based level index (displayed as index + 1). */
  index: number;
  cycle: number;
  startX: number;
  endX: number;
}

export const LEVEL_LENGTH_PX = 8000;
/** Each full pass through the templates raises the difficulty band by this much (capped at 1). */
export const CYCLE_DIFFICULTY_STEP = 0.12;

export const LEVEL_TEMPLATES: readonly LevelTemplate[] = [
  {
    name: 'Sunny Meadow',
    subtitle: 'pits and ledges — collect the bones',
    theme: {
      id: 'meadow',
      sky: [0xbcdcf5, 0xf3f1e8],
      grass: 0x6fbf55,
      soil: 0x8a6a4a,
      soilDark: 0x7a5c3f,
      stone: 0x9a9aa6,
      stoneDark: 0x6e6e7a,
      plank: 0xc98b4b,
      plankLight: 0xe6b877,
      accent: 0x2f7d32,
    },
    features: ['pit', 'ledge'],
    difficulty: [0.1, 0.4],
  },
  {
    name: 'Stone Quarry',
    subtitle: 'terraces, spikes, moving platforms, thorns, beetles and falling rocks',
    theme: {
      id: 'quarry',
      sky: [0xd9c9b0, 0xf6ead6],
      grass: 0xa8a070,
      soil: 0x8f7a63,
      soilDark: 0x74614d,
      stone: 0xb8b0a4,
      stoneDark: 0x7d7468,
      plank: 0xb07a45,
      plankLight: 0xd9a86c,
      accent: 0x8a4b1f,
    },
    features: ['pit', 'ledge', 'terrace', 'spikes', 'moving', 'roller', 'beetle', 'rock'],
    difficulty: [0.3, 0.6],
  },
  {
    name: 'Twilight Canopy',
    subtitle: 'crumbling ledges, springs and bats — golden bones up high',
    theme: {
      id: 'canopy',
      sky: [0x6c5b9e, 0xf2b5a0],
      grass: 0x4f9a5d,
      soil: 0x5f4a3d,
      soilDark: 0x4a392e,
      stone: 0x7f8a99,
      stoneDark: 0x53606e,
      plank: 0x9c6a35,
      plankLight: 0xc8955c,
      accent: 0xe0a458,
    },
    features: [
      'pit',
      'ledge',
      'terrace',
      'spikes',
      'moving',
      'crumble',
      'spring',
      'golden',
      'roller',
      'beetle',
      'bat',
    ],
    difficulty: [0.45, 0.75],
  },
  {
    name: 'Frozen Ridge',
    subtitle: 'ice underfoot, wind in your face — towers of golden bones',
    theme: {
      id: 'frozen',
      sky: [0x8fb8e0, 0xeaf4ff],
      grass: 0xe6f3ff,
      soil: 0x6b7a8a,
      soilDark: 0x56636f,
      stone: 0xc3d0dd,
      stoneDark: 0x8d9aa8,
      plank: 0x8a7a6a,
      plankLight: 0xb8a692,
      accent: 0x4c8dd6,
    },
    features: [
      'pit',
      'ledge',
      'terrace',
      'moving',
      'crumble',
      'golden',
      'roller',
      'beetle',
      'bat',
      'ice',
      'wind',
    ],
    difficulty: [0.55, 0.85],
  },
  {
    name: 'Midnight Run',
    subtitle: 'everything, faster',
    theme: {
      id: 'midnight',
      sky: [0x141a33, 0x3b3f6b],
      grass: 0x3d7a4f,
      soil: 0x3c3140,
      soilDark: 0x2a2230,
      stone: 0x5a6275,
      stoneDark: 0x3a404f,
      plank: 0x7a5a3a,
      plankLight: 0xa27b4e,
      accent: 0xffd23f,
    },
    features: [
      'pit',
      'ledge',
      'terrace',
      'spikes',
      'moving',
      'crumble',
      'spring',
      'golden',
      'roller',
      'beetle',
      'bat',
      'rock',
      'ice',
      'wind',
    ],
    difficulty: [0.6, 0.9],
  },
];

export function levelIndexAt(x: number): number {
  return Math.max(0, Math.floor(x / LEVEL_LENGTH_PX));
}

export function levelByIndex(index: number): LevelDef {
  const i = Math.max(0, Math.floor(index));
  const template = LEVEL_TEMPLATES[i % LEVEL_TEMPLATES.length]!;
  const cycle = Math.floor(i / LEVEL_TEMPLATES.length);
  return {
    ...template,
    index: i,
    cycle,
    startX: i * LEVEL_LENGTH_PX,
    endX: (i + 1) * LEVEL_LENGTH_PX,
  };
}

export function levelAt(x: number): LevelDef {
  return levelByIndex(levelIndexAt(x));
}

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Difficulty 0..1 at a world x: the level's band, ramped over the level, raised per cycle. */
export function difficultyAt(x: number): number {
  const level = levelAt(x);
  const progress = clamp01((x - level.startX) / (level.endX - level.startX));
  const [d0, d1] = level.difficulty;
  const bump = level.cycle * CYCLE_DIFFICULTY_STEP;
  return clamp01(d0 + (d1 - d0) * progress + bump);
}

/** Every problem with the level templates (empty = valid). */
export function levelProblems(): string[] {
  const problems: string[] = [];
  const ids = new Set<string>();
  LEVEL_TEMPLATES.forEach((t, i) => {
    if (ids.has(t.theme.id)) problems.push(`template ${i}: theme id ${t.theme.id} duplicated`);
    ids.add(t.theme.id);
    const [d0, d1] = t.difficulty;
    if (!(d0 >= 0 && d1 <= 1 && d0 <= d1)) problems.push(`template ${i}: difficulty band invalid`);
    if (!t.features.includes('pit')) problems.push(`template ${i}: every level needs pits`);
    if (t.features.includes('spring') && !t.features.includes('golden'))
      problems.push(`template ${i}: springs lead to golden bones`);
  });
  return problems;
}
